import { Collapse } from 'antd';
import type { Store } from 'antd/es/form/interface';
import { useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { BillingContact } from 'app/components/commons/BillingContact/BillingContact';
import { BookingContact } from 'app/components/commons/BookingContact/BookingContact';
import { GuestContact } from 'app/components/commons/GuestContact/GuestContact';
import type { Mode } from 'app/components/commons/Hotel/Types';
import { SaleContact } from 'app/components/commons/SaleContact/SaleContact';
import { FormSection } from 'app/components/forms/FormSection';
import { ChannelManager } from 'app/typings/channelManagers';
import type { Hotel } from 'app/typings/hotels/Hotel/Hotel';

import './Communication.scss';
import { ChannelManager as ChannelManagerComponent } from './_components/ChannelManager';

type Props = {
  mode: Mode;
  values: Hotel;
  setFieldsValue: (value: Store) => void;
  originalBillingEmails?: string[];
};

export const Communication = ({
  mode,
  values,
  setFieldsValue,
  originalBillingEmails,
}: Props) => {
  const communicationRef = useRef<null | HTMLDivElement>(null);
  const { search } = useLocation();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const hasChannelManager =
    !!values.channelManager && values.channelManager !== ChannelManager.NONE;

  useLayoutEffect(() => {
    const section = new URLSearchParams(search).get('section');

    if (!section) {
      return;
    }

    document
      .getElementById(section)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [search]);

  const isDisabled = mode === 'view';

  return (
    <div ref={communicationRef} className="communicationContainer">
      <FormSection title="Communication">
        <Collapse
          bordered={false}
          defaultActiveKey={[
            'sale-contact',
            'notif-contact',
            'guest-contact',
            'billing-contact',
          ]}
        >
          <SaleContact mode={mode} key="sale-contact" id="principal-contact" />

          <BookingContact
            key="notif-contact"
            id="booking-contact"
            mode={mode}
            notifEmails={values.notifEmails}
            billingEmails={values.billingEmails}
            setNotifEmails={(notifEmails) => {
              setFieldsValue({ ...values, notifEmails });
            }}
            channelManager={
              <ChannelManagerComponent
                mode={mode}
                values={values}
                setFieldsValue={setFieldsValue}
                isDisabled={isDisabled}
                hasChannelManager={hasChannelManager}
                openConfirmModal={openConfirmModal}
                setOpenConfirmModal={setOpenConfirmModal}
              />
            }
          />
          <GuestContact
            mode={mode}
            key="guest-contact"
            id="guest-contact"
            isOnAdmin
          />
          <BillingContact
            mode={mode}
            key="billing-contact"
            id="billing-contact"
            originalBillingEmails={originalBillingEmails}
            notifEmails={values.notifEmails}
          />
        </Collapse>
      </FormSection>
    </div>
  );
};
